import { z } from 'zod';
import { CargoImportFileFormatSchema } from './enums.js';

// Cargo Excel import wire contract (#1229). The importer ingests one or many
// already-uploaded `cargo-import-file` workbooks (NextKargo `Ян` /
// `Ян (полная машина)` formats) and returns TRANSIENT article-level result
// lists per file. INVARIANT: no import counters are persisted on `CargoFile`
// (the source records no import outcome — see cargo.prisma); the response below
// is the only place the per-file outcome surfaces.

/**
 * @aiPurpose one already-uploaded import file to process. `objectKey` is a
 *   `cargo-import-file/<userId>/<uuid>` storage key the actor previously
 *   minted; `format` selects the parser. The actor's ownership of the key is
 *   re-checked server-side (`StorageService.parseIssuedKey`), so a foreign
 *   key is rejected per-file rather than read.
 */
export const CargoImportFileInputSchema = z
  .object({
    objectKey: z.string().min(1),
    format: CargoImportFileFormatSchema,
  })
  .strict();
export type CargoImportFileInput = z.infer<typeof CargoImportFileInputSchema>;

// The max workbooks per import batch, enforced by both the request schema and the client.
export const CARGO_IMPORT_MAX_FILES = 20;

/**
 * @aiPurpose body of `POST /cargo/import`. A batch of 1..20 already-uploaded
 *   workbooks; each is processed independently and returns its own result.
 */
export const CargoImportRequestSchema = z
  .object({
    files: z.array(CargoImportFileInputSchema).min(1).max(CARGO_IMPORT_MAX_FILES),
  })
  .strict();
export type CargoImportRequest = z.infer<typeof CargoImportRequestSchema>;

/**
 * @aiPurpose one article-level result row. `article` is the source article
 *   (or null when the row had none), `cargoId` is the created/updated row id
 *   when persistence succeeded, `message` is a human-readable note for
 *   warnings/errors (e.g. duplicate skip, row-skip reason).
 */
export const CargoImportRowResultSchema = z
  .object({
    article: z.string().nullable(),
    cargoId: z.string().uuid().optional(),
    message: z.string().optional(),
  })
  .strict();
export type CargoImportRowResult = z.infer<typeof CargoImportRowResultSchema>;

/**
 * @aiPurpose per-file outcome. `fileId` is the bound `CargoFile` id (null only
 *   when the file failed BEFORE the binding could be created — e.g. foreign
 *   object key, missing object, `.xls` rejection). The four lists are
 *   article-level and transient — never persisted.
 */
export const CargoImportFileResultSchema = z
  .object({
    objectKey: z.string(),
    format: CargoImportFileFormatSchema,
    fileId: z.string().uuid().nullable(),
    created: z.array(CargoImportRowResultSchema),
    updated: z.array(CargoImportRowResultSchema),
    warnings: z.array(CargoImportRowResultSchema),
    errors: z.array(CargoImportRowResultSchema),
  })
  .strict();
export type CargoImportFileResult = z.infer<typeof CargoImportFileResultSchema>;

/**
 * @aiPurpose response of `POST /cargo/import`: one result per requested file,
 *   in request order.
 */
export const CargoImportResponseSchema = z
  .object({
    files: z.array(CargoImportFileResultSchema),
  })
  .strict();
export type CargoImportResponse = z.infer<typeof CargoImportResponseSchema>;
